import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import { setLanIsolationApi, type OpenwrtWiring } from '@/api/vendo';

/**
 * "Customers on a VLAN tag" — a main OrangePi / mini PC / Ruijie whose
 * customers reach the onboard port TAGGED (a Huawei 5V5 on the pisowifi
 * preset sends them on VLAN 22, a managed switch does the same) instead of
 * untagged.
 *
 * Why this exists (tracker A8, 2026-10-27): an operator moving 50 standalone
 * OrangePi units off a 5V5 preset wanted the main vendo on VLAN 22 — no
 * sub-vendo, no second NodeMCU, no USB adapter. The router agent (vlan.sh,
 * since 2026-08-02) adds eth0.<tag> to the customer bridge, so tagged
 * customers join the normal 10.0.0.x LAN and portal; untagged customers and
 * the NodeMCU keep working. The server endpoint existed since June (MikroTik
 * "LAN isolation", script v12); the app never had a control for it. Nothing
 * reboots, nothing moves: the router picks it up on its next check-in.
 *
 * Save style: no Save button. The switch saves at once; the number saves on
 * blur / Enter once it is valid (Ray, settings rule 2026-10-23).
 */

export const DEFAULT_CUSTOMER_VLAN = 22;

interface Props {
   deviceId: string;
   lanIsolation?: boolean;
   mainVlanId?: number;
   mainSubnet?: string;
   /** The internet's own tag on the same port (Internet VLAN tag), if any. */
   wanVlanId?: number | null;
   /** Sub-vendo VLAN zones on this router — the customer tag cannot be one. */
   zoneIds?: number[];
   /** When the router last confirmed the main VLAN is built; null = pending since the last save. */
   mainVlanLiveAt?: string | Date | null;
   /** The port the tag rides on, as saved (the server picks the internet port by itself). */
   trunkIface?: string;
   /** The router's internet port, to name the trunk plainly. */
   wanDev?: string;
   onRefresh?: () => void;
   /** MikroTik main (A51, Ray 2026-10-29): the same card on the Networks page —
    *  the tag rides the Trunk ports, no sub-vendo needed. */
   mikrotik?: {
      /** Trunk ports the router carries the tag on, e.g. ['ether4', 'ether5']. */
      trunkPorts: string[];
      /** 'mt-pending' while a save waits for the router's read-back. */
      liveConfig?: string;
      /** Sub-vendos ride the same layout: the switch cannot go off. */
      hasSubs?: boolean;
   };
}

/** Why a typed tag cannot be used, or null when it can. Exported for tests. */
export function customerVlanProblem(
   raw: string,
   wanVlanId?: number | null,
   zoneIds: number[] = []
): string | null {
   if (!/^\d+$/.test(raw)) return 'Type the VLAN number, a whole number 2–4094.';
   const v = Number(raw);
   if (v < 2 || v > 4094) return 'The VLAN number must be between 2 and 4094.';
   if (wanVlanId != null && wanVlanId > 0 && v === wanVlanId)
      return `${v} is the internet tag on this port. The customers need a different tag.`;
   if (zoneIds.includes(v)) return `VLAN ${v} is a sub-vendo VLAN on this router. Pick another.`;
   return null;
}

const CustomerVlanCard: React.FC<Props> = ({
   deviceId,
   lanIsolation,
   mainVlanId,
   mainSubnet,
   wanVlanId,
   zoneIds = [],
   mainVlanLiveAt,
   trunkIface,
   wanDev,
   onRefresh,
   mikrotik,
}) => {
   const savedOn = lanIsolation === true;
   const savedTag = typeof mainVlanId === 'number' && mainVlanId >= 2 ? String(mainVlanId) : '';

   const [on, setOn] = useState(savedOn);
   const [tag, setTag] = useState(savedTag || String(DEFAULT_CUSTOMER_VLAN));
   const [err, setErr] = useState<string | null>(null);
   const [saving, setSaving] = useState(false);
   // What the server last agreed to — so a blur with no change sends nothing.
   const [applied, setApplied] = useState<{ on: boolean; tag: string }>({
      on: savedOn,
      tag: savedTag,
   });
   // Which port the router carries the tag on, as the server answered — so the
   // operator never has to open the Networks page to find out (A45).
   const [wiring, setWiring] = useState<string | null>(null);
   // MikroTik: the save's own reply says "pending" at once, before the device
   // list refreshes; the props take over again as soon as they change.
   const [snap, setSnap] = useState<{ at: string | Date | null; config: string } | null>(null);
   useEffect(() => setSnap(null), [mainVlanLiveAt, mikrotik?.liveConfig]);
   const live = !!(snap ? snap.at : mainVlanLiveAt);
   // MikroTik: only a save made from this card waits for a read-back. A router
   // that was registered with isolation on (VLAN 10) has no stamp and is not
   // "pending" — it has run that way all along.
   const pending = mikrotik ? !live && (snap ? snap.config : mikrotik.liveConfig) === 'mt-pending' : !live;
   // Pending → live: while the switch is on and the router has not echoed the
   // layout yet, re-read the device every 10 s (up to 3 min) so the banner
   // flips on its own instead of asking the operator to reload (A45).
   const polls = useRef(0);
   useEffect(() => {
      if (!savedOn || !pending || !onRefresh) return;
      polls.current = 0;
      const t = setInterval(() => {
         polls.current += 1;
         if (polls.current > 18) return clearInterval(t);
         onRefresh();
      }, 10_000);
      return () => clearInterval(t);
   }, [savedOn, pending, mainVlanId, onRefresh]);
   const mtPorts = mikrotik?.trunkPorts.map((p) => p.replace('ether', 'ether ').replace('sfp-sfpplus', 'SFP+ ')) ?? [];
   const portLine = mikrotik
      ? mtPorts.length > 3
         ? `the ${mtPorts.length} Trunk ports`
         : mtPorts.length
           ? `the Trunk port${mtPorts.length > 1 ? 's' : ''} (${mtPorts.join(', ')})`
           : null
      : wiring ||
      (trunkIface
         ? `${trunkIface === wanDev ? 'the internet port' : 'the trunk'} (${trunkIface})`
         : null);

   useEffect(() => {
      setOn(savedOn);
      if (savedTag) setTag(savedTag);
      setApplied({ on: savedOn, tag: savedTag });
      setErr(null);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [deviceId, savedOn, savedTag]);

   const save = async (nextOn: boolean, nextTag: string) => {
      if (saving) return;
      setSaving(true);
      try {
         const r = await setLanIsolationApi(deviceId, {
            lanIsolation: nextOn,
            mainVlanId: Number(nextTag),
            mainSubnet: mainSubnet || '10.0.0.0/24',
         });
         setApplied({ on: nextOn, tag: nextTag });
         if (mikrotik) {
            const rr = (r as { device?: { router?: { mainVlanLiveAt?: string | null; mainVlanLiveConfig?: string } } }).device?.router;
            setSnap({ at: rr?.mainVlanLiveAt ?? null, config: rr?.mainVlanLiveConfig ?? (nextOn ? 'mt-pending' : '') });
         }
         setWiring(nextOn ? wiringLine(r.openwrt) : null);
         toast.success(
            mikrotik
               ? nextOn
                  ? `Saved. Sending VLAN ${nextTag} to the router now.`
                  : 'Saved. Sending to the router now.'
               : nextOn
                 ? `Saved. Customers on VLAN ${nextTag} join this vendo on the router's next check-in.`
                 : 'Saved. Customers arrive untagged again on the next check-in.',
            { autoClose: 4000 }
         );
         onRefresh?.();
      } catch (e: unknown) {
         const msg =
            (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
            'Could not save the customer VLAN tag.';
         toast.error(msg);
         setErr(msg);
      } finally {
         setSaving(false);
      }
   };

   const toggle = () => {
      if (saving) return;
      if (on && mikrotik?.hasSubs) {
         setErr('Your sub-vendos use this VLAN layout, so it stays on. You can still change the tag.');
         return;
      }
      const nextOn = !on;
      setOn(nextOn);
      setErr(null);
      if (!nextOn) {
         if (applied.on) void save(false, applied.tag || tag);
         return;
      }
      const problem = customerVlanProblem(tag, wanVlanId, zoneIds);
      if (problem) {
         // Switched on with nothing usable yet: wait for a number.
         setErr(problem);
         return;
      }
      void save(true, tag);
   };

   const commitTag = () => {
      const problem = customerVlanProblem(tag, wanVlanId, zoneIds);
      setErr(problem);
      if (problem || !on) return;
      if (applied.on && applied.tag === tag) return;
      void save(true, tag);
   };

   return (
      <Card>
         <Head>
            <div>
               <Title>{mikrotik ? 'Access Point VLAN' : 'Access Point VLAN (onboard port)'}</Title>
               <Sub>
                  {mikrotik
                     ? 'Your access points send customers on a VLAN tag (Huawei 5V5 preset: 22). Plug them into a Trunk port. Main vendo only, no sub-vendo needed.'
                     : 'Your modem or access point sends customers on a VLAN (Huawei 5V5 preset: 22). Main vendo only.'}
               </Sub>
            </div>
            <Switch
               type="button"
               role="switch"
               aria-checked={on}
               aria-label={mikrotik ? 'Access Point VLAN' : 'Access Point VLAN (onboard port)'}
               disabled={saving}
               $on={on}
               onClick={toggle}
            >
               <Knob />
            </Switch>
         </Head>

         {on && (
            <Row>
               <Label htmlFor="customer-vlan">VLAN tag</Label>
               <Input
                  id="customer-vlan"
                  inputMode="numeric"
                  value={tag}
                  disabled={saving}
                  $invalid={!!err}
                  onChange={(e) => {
                     setTag(e.target.value.replace(/[^0-9]/g, '').slice(0, 4));
                     setErr(null);
                  }}
                  onBlur={commitTag}
                  onKeyDown={(e) => {
                     if (e.key === 'Enter') {
                        e.preventDefault();
                        commitTag();
                     }
                  }}
               />
               <Hint>{mikrotik ? 'Not a sub-vendo VLAN. Customer ports stay plain.' : 'Not the internet tag, not a sub-vendo VLAN.'}</Hint>
            </Row>
         )}
         {err && <Err>{err}</Err>}

         {applied.on && !saving ? (
            <Banner $live={!pending} role="status">
               {pending && <Dot aria-hidden />}
               {!pending
                  ? `Live on the router — VLAN ${applied.tag}${portLine ? ` on ${portLine}` : ''}.`
                  : mikrotik
                    ? `Sending VLAN ${applied.tag} to the router… a big router takes a few minutes.`
                    : `Pending — VLAN ${applied.tag}${portLine ? ` on ${portLine}` : ''} saved, waiting for the router (about a minute).`}
            </Banner>
         ) : (
            <Status>
               {saving
                  ? 'Saving…'
                  : on
                    ? 'Type the tag and press Enter.'
                    : mikrotik
                      ? 'Off. Turning it on makes phones re-connect once (about a minute).'
                      : 'Off — customers arrive untagged.'}
            </Status>
         )}
      </Card>
   );
};

export default CustomerVlanCard;

/** "the internet port (eth0)" from the wiring the server answered with. */
export function wiringLine(w?: OpenwrtWiring | null): string | null {
   if (!w?.trunkIface) return null;
   const t = w.trunks.find((x) => x.iface === w.trunkIface);
   const name = t?.label ? t.label.replace(/\s*\(.*\)\s*$/, '') : 'the internet port';
   return `${name} (${w.trunkIface})`;
}

/* Same look as the Portal Theme progress banner: amber + blinking dot while
   pending, green once the router confirmed (Ray 2026-10-28). */
const Banner = styled.p<{ $live: boolean }>`
   display: flex;
   align-items: center;
   gap: 8px;
   margin: 0;
   padding: 9px 12px;
   border-radius: 8px;
   font-size: 12.5px;
   font-weight: 600;
   background: ${({ $live }) => ($live ? `${colors.SUCCESS}18` : `${colors.WARNING}1c`)};
   color: ${({ $live }) => ($live ? colors.SUCCESS : colors.ORANGE)};
   border: 1px solid currentColor;
`;
const Dot = styled.span`
   width: 8px;
   height: 8px;
   border-radius: 50%;
   background: currentColor;
   flex: none;
   animation: sf-vlan-blink 1s ease-in-out infinite;
   @keyframes sf-vlan-blink {
      50% {
         opacity: 0.25;
      }
   }
   @media (prefers-reduced-motion: reduce) {
      animation: none;
   }
`;
const Card = styled.section`
   display: flex;
   flex-direction: column;
   gap: 8px;
   padding: 10px 12px;
   border: 1px solid ${colors.GRAY11};
   border-radius: 10px;
   background: var(--app-surface);
`;

const Head = styled.div`
   display: flex;
   align-items: flex-start;
   justify-content: space-between;
   gap: 12px;
`;

const Title = styled.div`
   font-size: 13px;
   font-weight: 700;
`;

const Sub = styled.div`
   margin-top: 3px;
   font-size: 11.5px;
   line-height: 1.5;
   color: ${colors.GRAY5};
`;

const Switch = styled.button<{ $on: boolean }>`
   flex: 0 0 auto;
   width: 40px;
   height: 22px;
   border-radius: 12px;
   border: none;
   cursor: pointer;
   padding: 2px;
   display: flex;
   justify-content: ${({ $on }) => ($on ? 'flex-end' : 'flex-start')};
   background: ${({ $on }) => ($on ? colors.PRIMARY : colors.GRAY12)};
   transition: background 0.15s;
   &:disabled {
      opacity: 0.6;
      cursor: default;
   }
`;

const Knob = styled.span`
   width: 18px;
   height: 18px;
   border-radius: 50%;
   background: #fff;
`;

const Row = styled.div`
   display: flex;
   align-items: center;
   flex-wrap: wrap;
   gap: 8px 12px;
`;

const Label = styled.label`
   font-size: 12.5px;
   font-weight: 700;
`;

const Input = styled.input<{ $invalid?: boolean }>`
   width: 100%;
   padding: 9px 11px;
   border-radius: 8px;
   font-size: 13px;
   background: var(--app-surface);
   color: var(--app-text);
   border: 1px solid ${({ $invalid }) => ($invalid ? colors.DANGER : colors.GRAY11)};
`;

const Hint = styled.div`
   font-size: 11.5px;
   line-height: 1.5;
   color: ${colors.GRAY5};
`;

const Err = styled.div`
   font-size: 11.5px;
   color: ${colors.DANGER};
`;

const Status = styled.div`
   font-size: 11.5px;
   color: ${colors.GRAY4};
`;
